import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import SettingsWrapper from '~/apps/rulemanager/components/settings-wrapper/SettingsWrapper';
import { RuleInserPosition } from '~/apps/rulemanager/rulemanager-core';
import { RuleApiModel } from '~/lib/api/rulemanager/types';
import { useRuleManagerApi } from '~/lib/api/rulemanager/use-rulemanager-api';
import { RuleNames } from '../../../../smartsend/rules/RuleGenerator';
import { generateDefaultDimensionsRule, generateDefaultWeightRule } from '~/apps/rulemanager/lib/helpers/RuleGenerator';
import { Customer, Dimensions } from '~/lib/api/smartsend/types';
import { P2GBox, P2GButton, P2GChip, P2GContainer, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { SidePanelContent } from '~/components/sidepanel-section';
import { AppSurface } from '~/components/surface/app-surface';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { OnboardingTitle } from './onboarding-title';
import { DimensionsSelection } from '~/apps/smartsend/components/smartsend/DimensionsSelection';
import Loading from '~/components/loading/Loading';
import OnboardingScreenSpacing from '../spacing-consts';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { useTranslation } from 'react-i18next';
import { useMeasurementSystem } from '~/hooks/use-measurement-system';
import {
  LocalUnitsEntry,
  formatDimensionsString,
  formatWeightString
} from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { TranslationNamespaces } from '~/lib/constants';
const LazyCmsContent = lazy(() => import('~/components/content/cms-content'));

type props = {
  isXs: boolean;
  onDone: (added: string[]) => void;
  children?: React.ReactNode;
  customer?: Customer;
  initialDimensions?: Dimensions & { Name?: string };
  rulesToInclude: string[];
  addRules: (newRules: RuleApiModel[], position: RuleInserPosition) => Promise<boolean>;
};

const Message = ({
  dimensions,
  localUnitsEntry
}: {
  dimensions: Dimensions & { Name?: string };
  localUnitsEntry?: LocalUnitsEntry;
}) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { unit } = useMeasurementSystem();
  // A named package and a bare set of dimensions read the same way in this sentence, so both feed
  // one key rather than two near-identical ones for translators to keep in step.
  const parcel = dimensions.Name
    ? dimensions.Name
    : formatDimensionsString(
        { length: dimensions?.Length, width: dimensions?.Width, height: dimensions?.Height },
        unit,
        { localUnitsEntry }
      );
  return (
    <>
      <P2GText variant="subtitle2" component="div">
        {t('SETUP_DIMENSIONS_RULES_SUMMARY', {
          defaultValue: "We'll use {{parcel}} ({{weight}}) when the weight or dimensions are missing from the order.",
          parcel,
          weight: formatWeightString(dimensions?.Weight, unit, { localUnitsEntry })
        })}
      </P2GText>
      <P2GText>
        {t('SETUP_DIMENSIONS_RULES_SUMMARY_HINT', {
          defaultValue: 'You can adjust these later from your dashboard.'
        })}
      </P2GText>
    </>
  );
};

const SmartSendOnboardingDimensionsRules = ({ isXs, initialDimensions, onDone, addRules, rulesToInclude }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { unit } = useMeasurementSystem();
  const { getpackages } = useRuleManagerApi();
  const [editDimensions, setEditDimensions] = useState<boolean>(false);
  const [editWeight, setEditWeight] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<(Dimensions & { Name?: string }) | null>(initialDimensions);
  // What the customer actually typed, when they typed it in imperial. Shared by both editors
  // below: each spreads the entry it is handed, so the weight editor cannot clobber the dimension
  // originals (or vice versa), and the read-only summaries show the entered value rather than one
  // round-tripped through the metric source of truth.
  const [localUnitsEntry, setLocalUnitsEntry] = useState<LocalUnitsEntry | undefined>(undefined);

  const getPackage = useCallback(async () => {
    const res = await getpackages();
    if (res.success) {
      return res.result.map((m) => ({
        Weight: undefined,
        Height: m.height || 0,
        Width: m.width || 0,
        Length: m.length || 0,
        Provider: '',
        Id: String(m.id),
        Name: m.name ?? '',
        Thumb: ''
      }));
    } else {
      return [];
    }
  }, [getpackages]);

  const onAcceptDefaults = async () => {
    const rules = [];

    if (dimensions && dimensions.Height && dimensions.Width && dimensions.Length) {
      if (!rules.some((x) => x.name === RuleNames.Dimensions)) {
        rules.push(generateDefaultDimensionsRule(dimensions.Height, dimensions.Width, dimensions.Length));
      }
    }

    if (dimensions?.Weight > 0) {
      if (!rules.some((x) => x.name === RuleNames.Weight)) {
        rules.push(generateDefaultWeightRule(dimensions.Weight));
      }
    }

    if (rules.length) {
      await addRules(rules, 'first');
    }

    onDone(rules.map((r) => r.name));
  };

  const buttonIsDisabled = useMemo(() => {
    if (
      rulesToInclude.includes(RuleNames.Dimensions) &&
      (!dimensions?.Height || !dimensions?.Width || !dimensions?.Length)
    ) {
      return true;
    }

    if (rulesToInclude.includes(RuleNames.Weight) && (!dimensions?.Weight || dimensions?.Weight <= 0)) {
      return true;
    }
  }, [dimensions, rulesToInclude]);

  return (
    <SettingsWrapper
      overrideLoading={
        <>
          <OnboardingScreenContentWrapper>
            <P2GContainer maxWidth="md">
              <SidePanelContent>
                <OnboardingTitle
                  text="Your default parcel <span>size</span>"
                  translationKey="SETUP_DIMENSIONS_RULES_TITLE"
                />
                <P2GText variant="subtitle1">
                  {t('SETUP_DIMENSIONS_RULES_LOADING', { defaultValue: 'Just getting things ready for you...' })}
                </P2GText>
                <Loading />
              </SidePanelContent>
            </P2GContainer>
          </OnboardingScreenContentWrapper>
          <OnboardingActionBar>
            <P2GText variant="subtitle2" component="div">
              {t('SETUP_DIMENSIONS_RULES_LOADING', { defaultValue: 'Just getting things ready for you...' })}
            </P2GText>
          </OnboardingActionBar>
        </>
      }
    >
      <OnboardingScreenContentWrapper>
        <P2GContainer maxWidth="md">
          <SidePanelContent>
            <OnboardingTitle
              text="Your default parcel <span>size</span>"
              translationKey="SETUP_DIMENSIONS_RULES_TITLE"
            />
            <P2GText variant="subtitle1">
              {t('SETUP_DIMENSIONS_RULES_SUBTITLE', {
                defaultValue:
                  'Let’s create defaults to automatically fill in missing weight or dimensions and make shipping smoother and faster.'
              })}
            </P2GText>
          </SidePanelContent>
        </P2GContainer>
        <P2GBox sx={OnboardingScreenSpacing} textAlign="left">
          <P2GContainer maxWidth="md">
            <SidePanelContent>
              {rulesToInclude.includes(RuleNames.Dimensions) && (
                <AppSurface>
                  <P2GGrid container direction="row" wrap="nowrap" spacing={4}>
                    {!isXs && (
                      <Suspense>
                        <LazyCmsContent
                          contentKey="smartsend-setup-rules-dimensions-image"
                          wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
                        />
                      </Suspense>
                    )}
                    <P2GGrid item flexGrow={1}>
                      <P2GBox display="flex" flexDirection="column" gap={2}>
                        <P2GBox display="flex" justifyContent="space-between">
                          <P2GText variant="subtitle1" fontWeight="bold">
                            {t('SETUP_DIMENSIONS_RULES_SIZE_TITLE', { defaultValue: 'Parcel Size' })}
                          </P2GText>
                          {!editDimensions && (
                            <P2GLink onClick={() => setEditDimensions(true)}>
                              {t('CUSTOMISE_LINK', { defaultValue: 'Customise' })}
                            </P2GLink>
                          )}
                        </P2GBox>
                        <P2GBox textAlign="left">
                          <P2GText variant="subtitle2">
                            {dimensions.Length && dimensions.Height && dimensions.Width
                              ? formatDimensionsString(
                                  { length: dimensions.Length, width: dimensions.Width, height: dimensions.Height },
                                  unit,
                                  { localUnitsEntry }
                                )
                              : '-'}
                          </P2GText>
                        </P2GBox>
                        {editDimensions ? (
                          <DimensionsSelection
                            startMinimised={false}
                            dimensions={dimensions}
                            localUnitsEntry={localUnitsEntry}
                            onUpdate={(d, entry) => {
                              setDimensions({ ...d, Weight: dimensions?.Weight });
                              setLocalUnitsEntry(entry);
                            }}
                            hideWeight={true}
                            dimensionsRequired={true}
                            hideDimensionsIfNotRequired={false}
                            getPackages={getPackage}
                          />
                        ) : (
                          <P2GBox>
                            <P2GChip
                              color="success"
                              label={t('SETUP_DIMENSIONS_RULES_DEFAULT_CHIP_LABEL', {
                                defaultValue: 'Default value applied, edit only if needed'
                              })}
                            />
                          </P2GBox>
                        )}
                      </P2GBox>
                    </P2GGrid>
                  </P2GGrid>
                </AppSurface>
              )}

              {rulesToInclude.includes(RuleNames.Weight) && (
                <AppSurface>
                  <P2GGrid container direction="row" wrap="nowrap" spacing={4}>
                    {!isXs && (
                      <Suspense>
                        <LazyCmsContent
                          contentKey="smartsend-setup-rules-weight-image"
                          wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
                        />
                      </Suspense>
                    )}
                    <P2GGrid item flexGrow={1}>
                      <P2GBox display="flex" flexDirection="column" gap={2}>
                        <P2GBox display="flex" justifyContent="space-between">
                          <P2GText variant="subtitle1" fontWeight="bold">
                            {t('SETUP_DIMENSIONS_RULES_WEIGHT_TITLE', { defaultValue: 'Parcel Weight' })}
                          </P2GText>
                          {!editWeight && (
                            <P2GLink onClick={() => setEditWeight(true)}>
                              {t('CUSTOMISE_LINK', { defaultValue: 'Customise' })}
                            </P2GLink>
                          )}
                        </P2GBox>
                        <P2GBox textAlign="left">
                          <P2GText variant="subtitle2">
                            {formatWeightString(dimensions.Weight, unit, { localUnitsEntry })}
                          </P2GText>
                        </P2GBox>
                        {editWeight ? (
                          <DimensionsSelection
                            startMinimised={false}
                            dimensions={dimensions}
                            localUnitsEntry={localUnitsEntry}
                            onUpdate={(d, entry) => {
                              setDimensions({ ...dimensions, Weight: d.Weight });
                              setLocalUnitsEntry(entry);
                            }}
                            hideWeight={false}
                            dimensionsRequired={false}
                            hideDimensionsIfNotRequired={true}
                            getPackages={getPackage}
                          />
                        ) : (
                          <P2GBox>
                            <P2GChip
                              color="success"
                              label={t('SETUP_DIMENSIONS_RULES_DEFAULT_CHIP_LABEL', {
                                defaultValue: 'Default value applied, edit only if needed'
                              })}
                            />
                          </P2GBox>
                        )}
                      </P2GBox>
                    </P2GGrid>
                  </P2GGrid>
                </AppSurface>
              )}
            </SidePanelContent>
            {isXs && (
              <P2GBox mt={2}>
                <AppSurface>
                  <P2GBox textAlign="center">
                    <Message dimensions={dimensions} localUnitsEntry={localUnitsEntry} />
                  </P2GBox>
                </AppSurface>
              </P2GBox>
            )}
          </P2GContainer>
        </P2GBox>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar
        next={
          <P2GButton
            size="large"
            variant="primary"
            fullWidth={isXs}
            disabled={buttonIsDisabled}
            onClick={onAcceptDefaults}
            style={{ minWidth: 'unset' }}
          >
            {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
          </P2GButton>
        }
        skip={
          <P2GButton variant="text" onClick={() => onDone([])}>
            {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
          </P2GButton>
        }
      >
        {!isXs && <Message dimensions={dimensions} localUnitsEntry={localUnitsEntry} />}
      </OnboardingActionBar>
    </SettingsWrapper>
  );
};

export default SmartSendOnboardingDimensionsRules;
